
import React from "react";
import { Button } from "@/components/ui/button";
import { Heart, Calendar, Gift, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import CalendlyWidget from "./CalendlyWidget";
import TrustBadges from "./TrustBadges";

const NewHero = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      headline: "Offrez un Cadeau Qui Grandit Avec Eux. Pour Toujours.",
      subheadline: "Commencez à offrir dès aujourd'hui pour ceux que vous aimez.",
      calendarButtonText: "Parler à un conseiller",
      gridItems: [
        { icon: Heart, text: "Transmettez vos émotions" },
        { icon: Calendar, text: "Choisissez le moment" },
        { icon: Gift, text: "Une contribution investie" },
        { icon: Star, text: "Créez un moment unique" }
      ]
    },
    en: {
      headline: "Give a Gift That Grows With Them. Forever.",
      subheadline: "Start gifting today for the ones you love.",
      calendarButtonText: "Speak with an advisor",
      gridItems: [
        { icon: Heart, text: "Transmit your emotions" },
        { icon: Calendar, text: "Choose the moment" },
        { icon: Gift, text: "Financial contribution growing with them" },
        { icon: Star, text: "Create a unique moment" }
      ]
    },
    de: {
      headline: "Schenken Sie ein Geschenk, das mit ihnen wächst. Für immer.",
      subheadline: "Beginnen Sie noch heute mit dem Schenken für Ihre Liebsten.",
      calendarButtonText: "Sprechen Sie mit einem Berater",
      gridItems: [
        { icon: Heart, text: "Übertragen Sie Ihre Gefühle" },
        { icon: Calendar, text: "Wählen Sie den Moment" },
        { icon: Gift, text: "Finanzielle Unterstützung, die mitwächst" },
        { icon: Star, text: "Schaffen Sie einen einzigartigen Moment" }
      ]
    }
  };

  return (
    <section className="pt-32 pb-20 md:pt-40 md:pb-24 px-4 bg-gradient-to-br from-legatia-light to-white overflow-hidden relative">
      <div className="absolute top-20 right-[10%] w-64 h-64 bg-legatia-orange/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-[5%] w-40 h-40 bg-legatia-yellow/20 rounded-full blur-2xl"></div>
      
      <div className="container mx-auto text-center relative z-10">
        <p className="text-legatia-orange font-medium mb-4 animate-fade-in">Une présence qui traverse le temps</p>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in">
          <span className="gradient-text">{content[language].headline}</span>
        </h1>
        
        <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          {content[language].subheadline}
        </p>
        
        <div className="max-w-3xl mx-auto grid md:grid-cols-4 gap-4 mb-10 animate-fade-in" style={{ animationDelay: "0.4s" }}>
          {content[language].gridItems.map((item, index) => (
            <div key={index} className="flex items-center space-x-2 p-4 rounded-lg bg-white/80 shadow-sm">
              <item.icon className={`text-legatia-${['orange', 'yellow', 'green', 'red'][index]} h-6 w-6`} />
              <p>{item.text}</p>
            </div>
          ))}
        </div>
        
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 animate-fade-in" style={{ animationDelay: "0.5s" }}>
          {[
            { number: "1000+", label: language === 'fr' ? "Familles" : language === 'en' ? "Families" : "Familien" },
            { number: "15M", label: "€" },
            { number: "4.9/5", label: language === 'fr' ? "Satisfaction" : language === 'en' ? "Satisfaction" : "Zufriedenheit" },
            { number: "12+", label: language === 'fr' ? "Années d'expertise" : language === 'en' ? "Years expertise" : "Jahre Expertise" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-4">
              <p className="text-2xl md:text-3xl font-bold text-legatia-orange mb-1">{stat.number}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>
        
        <div className="relative max-w-4xl mx-auto mb-16 animate-fade-in" style={{ animationDelay: "0.6s" }}>
          <div className="bg-white/90 border border-gray-100 p-6 md:p-8 rounded-2xl shadow-lg mb-10">
            <div className="grid md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-2 order-2 md:order-1">
                <div className="relative">
                  <div className="w-full aspect-square bg-legatia-orange/20 rounded-lg transform rotate-3 absolute"></div>
                  <img 
                    src="/lovable-uploads/edb21411-962a-4c80-83dc-c58597759057.png" 
                    alt="Jeune femme découvrant un message vidéo de ses parents" 
                    className="w-full aspect-square object-cover rounded-lg relative z-10 shadow-md"
                  />
                  <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-legatia-yellow rounded-full z-20 flex items-center justify-center">
                    <Heart className="text-white h-6 w-6" />
                  </div>
                </div>
              </div>
              
              <div className="md:col-span-3 order-1 md:order-2">
                <p className="font-medium text-gray-800 mb-4">
                  <span className="font-bold">✨ Imaginez...</span> Votre fille, le jour de son bac,
                  découvre un message vidéo que vous avez enregistré pour elle il y a 15 ans.
                  Vous lui parlez comme si vous étiez là aujourd'hui, partageant toute la fierté que vous ressentez.
                  Ce n'est pas simplement un souvenir, c'est votre présence qui a traversé le temps pour être avec elle à ce moment précis.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-8">
          <Button 
            className="btn-primary text-lg px-8 py-6 animate-fade-in" 
            style={{ animationDelay: "0.8s" }}
            onClick={() => window.location.href = "https://app.getlegatia.ch/"}
          >
            Créer une capsule temporelle émotionnelle
          </Button>
      
          <CalendlyWidget url="https://calendly.com/legatia/15min" />
        </div>

        <div className="mt-8 animate-fade-in" style={{ animationDelay: "1s" }}>
          <TrustBadges />
        </div>
      </div>
    </section>
  );
};

export default NewHero;
